import { getApiUrl, getCsrfToken, getProdJobsUrl, assertTestAllowed, readJobsApiTarget, registerProdRunnerJob, startLocalHybridJob } from "./apiHelper";
import { isValidTargetUrl } from "../component/targets/UrlInput";
import { appendCustomTargetFormData, CUSTOM_TARGET_PY_SCRIPTS } from "../component/targets/customTargetHelpers";
import i18n from "../i18n";

async function createQuickStartTarget({
  name,
  url,
  apiConfigMode = CUSTOM_TARGET_PY_SCRIPTS,
  promptsProcessing = "single",
  inputScriptFile = null,
  outputScriptFile = null,
  requestParams = "",
  responseParams = "",
  advancedOptions = null,
}) {
  if (!name?.trim()) {
    throw new Error(i18n.t("targetNameRequired", { ns: "quickStart" }));
  }

  const formData = new FormData();
  formData.append("target_name", name.trim());
  formData.append(
    "target_desc",
    i18n.t("createdViaWizardDesc", { ns: "quickStart" })
  );
  formData.append("target_type", "api_target");

  const targetUrl = (url || "").trim();
  if (!targetUrl || !isValidTargetUrl(targetUrl)) {
    throw new Error(i18n.t("alerts.invalidUrl", { ns: "targets" }));
  }

  appendCustomTargetFormData(formData, {
    url: targetUrl,
    apiConfigMode,
    promptsProcessing,
    requestParams,
    responseParams,
    advancedOptions,
    inputScriptFile,
    outputScriptFile,
  });

  const csrfToken = await getCsrfToken();
  const response = await fetch(getApiUrl("/api/internal/target-config/"), {
    method: "POST",
    headers: { "X-CSRFToken": csrfToken },
    credentials: "include",
    body: formData,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) {
    throw new Error(
      data.detail || data.error || i18n.t("alerts.createFailed", { ns: "targets" })
    );
  }

  return data.data.id;
}

async function createQuickStartProfiles({
  targetId,
  tests,
  aiAppType,
  industry,
  subIndustry,
}) {
  const csrfToken = await getCsrfToken();
  const response = await fetch(getApiUrl("/api/internal/quick-start/create-profile/"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken,
    },
    credentials: "include",
    body: JSON.stringify({
      target_id: targetId,
      ai_app_type: aiAppType,
      industry,
      sub_industry: subIndustry,
      tests,
    }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(
      data.detail || data.error || i18n.t("createProfilesFailed", { ns: "quickStart" })
    );
  }

  return data.profiles;
}

async function runQuickStartTests(profiles, reportName = "") {
  const csrfToken = await getCsrfToken();
  const trimmedReportName = (reportName || "").trim() || "Quick Start Report";
  const reportNameWithTime = `${trimmedReportName} ${new Date().toLocaleString()}`;

  for (const profileId of profiles) {
    const formData = new FormData();
    formData.append("profile_id", String(profileId));
    formData.append("report_name", reportNameWithTime);

    if (readJobsApiTarget() === "localhost") {
      const registered = await registerProdRunnerJob(formData);
      await startLocalHybridJob({
        profileId,
        reportName: reportNameWithTime,
        registered,
      });
    } else {
      await fetch(getProdJobsUrl(""), {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrfToken },
        body: formData,
      });
    }
  }
}

/**
 * Quick Start checkboxes do not map 1:1 to jobs.
 * red_teaming_prompts and/or malicious_docs → one prompts_docs_llm profile/job.
 */
function countQuickStartJobs(tests = {}) {
  let count = 0;
  if (tests.red_teaming_prompts || tests.malicious_docs) count += 1;
  if (tests.ai_specific_dos) count += 1;
  if (tests.regulation_compliance) count += 1;
  return count;
}

/**
 * 1. create target
 * 2. create profiles
 * 3. start a job per profile id
 */
export async function runQuickStart({
  aiAppType,
  industry,
  subIndustry,
  target,
  tests,
  reportName = "",
}) {
  const jobCount = countQuickStartJobs(tests);
  if (jobCount === 0) {
    throw new Error(i18n.t("selectAtLeastOneTest", { ns: "quickStart" }));
  }

  // Block the whole Quick Start if current + resulting jobs would exceed max
  await assertTestAllowed(jobCount);

  const targetId = await createQuickStartTarget(target);
  const profiles = await createQuickStartProfiles({
    targetId,
    tests,
    aiAppType,
    industry,
    subIndustry,
  });
  await runQuickStartTests(profiles, reportName);
}
